import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { StyleSheetManager, ThemeProvider } from "styled-components";
import THEME from "theme";

import isPropValid from "@emotion/is-prop-valid";

import GlobalStyling from "components/atoms/GlobalStyling";
import MainLayout from "components/organisms/MainLayout";
import { useQuery } from "hooks/useParamsState";
import EditIntercomSegment from "pages/Configuration/IntercomSegments/EditIntercomSegment";
import IntercomSegmentsPage from "pages/Configuration/IntercomSegments/IntercomSegmentsPage";
import ContentPage from "pages/Content/FeedCards/ContentPage";
import CreateContentPage from "pages/Content/FeedCards/CreateContentPage";
import EditContentPage from "pages/Content/FeedCards/EditContentPage";
import EditInfoTextPage from "pages/Content/InfoTextPages/EditInfoTextPage";
import InfoTextListPage from "pages/Content/InfoTextPages/InfoTextListPage";
import ServiceMessageFormPage from "pages/Content/ServiceMessages/ServiceMessageFormPage";
import ServiceMessagesPage from "pages/Content/ServiceMessages/ServiceMessagesPage";
import VilDuViteMerListPage from "pages/Content/VilDuViteMer/VilDuViteMerListPage";
import UpsertVilDuViteMerPage from "pages/Content/VilDuViteMer/VilDuViteMerPage";
import EditFeatureFlagPage from "pages/Features/Feature flags/EditFeatureFlag";
import FeatureFlagsPage from "pages/Features/Feature flags/FeatureFlagsPage";
import EditGroupPage from "pages/Features/Grupper/EditGroupPage";
import GroupsPage from "pages/Features/Grupper/GroupsPage";
import SingleGroupPage from "pages/Features/Grupper/SingleGroupPage";
import { SendNotificationsPage } from "pages/Features/Send push/SendNotificationsPage";
import { StatisticsPage } from "pages/Features/Statistikk/StatisticsPage";
import LoginPage from "pages/LoginPage";
import NotFound from "pages/NotFound";
import ProductsTopPage from "pages/Productadmin/ProductsTopPage";
import CreateSikringPage from "pages/Sikring/opprett/CreateSikringPage";
import SikringsordreListPage from "pages/Sikring/oversikt/SikringsordreListPage";
import { OrdersPage, ReferralsPage } from "pages/Strombestilling";
import OfferNewPage from "pages/Strombestilling/orderoffers/OrderOfferNewPage";
import StromtilbudPage from "pages/Strombestilling/orderoffers/OrderOffersPage";
import { SendOrderLinkPage } from "pages/Strombestilling/orders/SendOrderLinkPage";
import UserPage from "pages/Users/UserPage";
import UsersPage from "pages/Users/UsersPage";

function App() {
	const auth = useAuth();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const query = useQuery();

	useEffect(() => {
		const isLocalHost = window.location.href.includes("localhost");
		const prefix = isLocalHost ? "http" : "https";
		if (auth.isLoading) return;
		const providerId = auth?.user?.profile?.providerId;
		if (providerId && providerId !== "ActiveDirectory") {
			console.warn(`providerId ${providerId}. Logger ut!`);
			console.warn(auth?.user?.profile);
			auth.signoutRedirect({
				post_logout_redirect_uri: `${prefix}://${window.location.host}/`,
			});
			return;
		}
		if (!window.location.href.indexOf("?")) {
			return;
		}

		// Ensure that the get variables from keycloak wont persist.
		if (query.has("state")) query.delete("state");
		if (query.has("code")) query.delete("code");
		if (query.has("session_state")) query.delete("session_state");
		if (query.has("iss")) query.delete("iss");

		navigate(
			{
				pathname,
				search: query.toString(),
			},
			{ replace: true }
		);
	}, [auth, pathname, query, navigate]);

	return (
		<StyleSheetManager
			shouldForwardProp={(prop, element) => {
				// Forward props to styled raw HTML components automatically
				// See https://styled-components.com/docs/faqs#what-do-i-need-to-do-to-migrate-to-v6
				return typeof element === "string" ? isPropValid(prop) : true;
			}}
		>
			<ThemeProvider theme={THEME}>
				<GlobalStyling />
				<Routes>
					<Route>
						{auth.isAuthenticated ? (
							<Route path="/" element={<MainLayout />}>
								<Route index element={<UsersPage />} />

								{/* Brukere */}
								<Route path="/users" element={<UsersPage />} />
								<Route path="/users/:userId" element={<UserPage />} />

								{/* INNHOLD */}
								{/* Feedkort */}
								<Route path="/content" element={<ContentPage />} />
								<Route path="/content/create" element={<CreateContentPage />} />
								<Route path="/content/:contentId" element={<EditContentPage />} />
								{/* Hjelpesider */}
								<Route path="/vil-du-vite-mer" element={<VilDuViteMerListPage />} />
								<Route path="/vil-du-vite-mer/create" element={<UpsertVilDuViteMerPage />} />
								<Route path="/vil-du-vite-mer/:id" element={<UpsertVilDuViteMerPage />} />
								{/* Infotekster */}
								<Route path="/info-text" element={<InfoTextListPage />} />
								<Route path="/info-text/create" element={<EditInfoTextPage />} />
								<Route path="/info-text/:infoTextId" element={<EditInfoTextPage />} />
								{/* Driftsmeldinger */}
								<Route path="/service-messages" element={<ServiceMessagesPage />} />
								<Route path="/service-messages/create" element={<ServiceMessageFormPage />} />
								<Route path="/service-messages/:serviceMessageId" element={<ServiceMessageFormPage />} />

								{/* FEATURES */}
								{/* Feature flags */}
								<Route path="/features" element={<FeatureFlagsPage />} />
								<Route path="/features/:featureId" element={<EditFeatureFlagPage />} />
								<Route path="/statistics" element={<StatisticsPage />} />
								<Route path="/notifications" element={<SendNotificationsPage />} />
								{/* Grupper */}
								<Route path="/groups" element={<GroupsPage />} />
								<Route path="/groups/:groupId" element={<SingleGroupPage />} />
								<Route path="/groups/:groupId/edit" element={<EditGroupPage />} />
								{/* Intercom segmenter */}
								<Route path="/intercom-segments" element={<IntercomSegmentsPage />} />
								<Route path="/intercom-segments/:intercomSegmentId" element={<EditIntercomSegment />} />

								{/* Produktadmin */}
								<Route path="/products/*" element={<ProductsTopPage />} />

								{/* Bestilling */}
								<Route path="/orders/offers/:offerId" element={<OfferNewPage />} />
								<Route path="/orders/offers" element={<StromtilbudPage />} />
								<Route path="/orders/all" element={<OrdersPage />} />
								<Route path="/orders/referrals" element={<ReferralsPage />} />
								<Route path="/orders/send-link" element={<SendOrderLinkPage />} />
								<Route path="/orders/new" element={<Navigate to="/orders/send-link" replace />} />

								{/* Sikring */}
								<Route path="/sikring" element={<SikringsordreListPage />} />
								<Route path="/sikring/ny" element={<CreateSikringPage />} />

								<Route path="/*" element={<NotFound />} />
							</Route>
						) : (
							<Route path="/*" element={<LoginPage />} />
						)}
					</Route>
				</Routes>
			</ThemeProvider>
		</StyleSheetManager>
	);
}

export default App;
